import { useState, useRef, useEffect } from "react";
import Topbar from "../../../components/Topbar/Topbar";
import { toggle_off, toggle_on, upload_img } from "../../../assets/img";
import { get, post } from "../../../Services/ApiHandler";
import { URLS } from "../../../Services/Constant";
import { toast } from "react-toastify";

const MAX_UPLOAD_SIZE_MB = 35;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

const CheckoutReview = () => {
  const fileInputRef = useRef(null);

  const [reviewData, setReviewData] = useState({
    title: "",
    description: "",
    image: "",
    toggle: false,
  });

  const [imagePreview, setImagePreview] = useState("");
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  const fetchData = async () => {
    let res = await get(URLS.VIEW_CHECKOUT_REVIEW);
    let { status, data } = res ?? {};
    if (status === "success") {
      let { name, icon, status, banner_image } = data ?? {};
      setReviewData({
        title: name,
        description: icon,
        image: banner_image,
        toggle: status,
      });
      setImagePreview(banner_image);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    let { name, value } = e.target;
    setReviewData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // clear error for this field as user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBannerClick = () => {
    if (!isEditing) return;
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // validate type
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        image: "Please upload a valid image file",
      }));
      e.target.value = "";
      return;
    }

    // validate size
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      setErrors((prev) => ({
        ...prev,
        image: `Image size should not exceed ${MAX_UPLOAD_SIZE_MB}MB`,
      }));
      e.target.value = "";
      return;
    }

    setReviewData((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
    setErrors((prev) => ({ ...prev, image: "" }));

    // reset input value so selecting the same file again still fires onChange
    e.target.value = "";
  };

  const validate = () => {
    const newErrors = {};

    if (!reviewData.title?.trim()) {
      newErrors.title = "Section title is required";
    }

    if (!reviewData.description?.trim()) {
      newErrors.description = "Section description is required";
    }

    if (!reviewData.image) {
      newErrors.image = "Please upload a banner image";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    let formdata = new FormData();
    formdata.append("name", reviewData.title);
    formdata.append("description", reviewData.description);
    formdata.append("banner_image", reviewData.image);
    formdata.append("status", reviewData.toggle);
    let res = await post(URLS.CHECKOUT_REVIEW, formdata);
    if (res?.status === "success") {
      toast.success(res?.message);
      fetchData();
    }
  };

  let styleDisabled = {
    opacity: isEditing ? 1 : 0.5,
    cursor: isEditing ? "pointer" : "not-allowed",
  };

  return (
    <div>
      <Topbar header={"Master"} subHead={"Checkout Review"} />

      <hr />

      <div className="review-bg">
        <div className="d-flex ac-je">
          <img
            src={reviewData?.toggle ? toggle_on : toggle_off}
            className={`roles-icon ${isEditing ? "pointer" : ""}`}
            alt="toggle"
            style={styleDisabled}
            onClick={() => {
              if (!isEditing) return;
              setReviewData((prev) => ({ ...prev, toggle: !prev.toggle }));
            }}
          />
        </div>

        <div className="d-flex ac-jc gap-5">
          <div className="col-md-5">
            <p className="f3 mb-2">Upload Banner</p>

            <div
              className={`banner-container ${isEditing ? "pointer" : ""}`}
              onClick={handleBannerClick}
              style={styleDisabled}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="banner preview"
                  className="banner-preview-img"
                />
              ) : (
                <>
                  <p className="f2 mb-1">
                    Choose a file or drag & drop it here
                  </p>
                  <img src={upload_img} className="upload-img" alt="upload" />
                </>
              )}

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </div>

            {errors.image && <p className="err">{errors.image}</p>}

            <h4 style={{ fontSize: 12 }} className="err mt-2">
              Max Upload Size : {MAX_UPLOAD_SIZE_MB}MB
            </h4>
          </div>

          <div className="col-md-5">
            <p className="f3 mb-1">Section Title</p>
            <input
              className="input-box"
              name="title"
              value={reviewData.title}
              onChange={handleChange}
              type="text"
              placeholder="Enter review title"
              disabled={!isEditing}
            />
            {errors.title && <p className="err">{errors.title}</p>}

            <p className="f3 mb-1">Section Description</p>
            <textarea
              className="input-box"
              name="description"
              value={reviewData.description}
              onChange={handleChange}
              placeholder="Enter review description"
              disabled={!isEditing}
            />
            {errors.description && <p className="err">{errors.description}</p>}

            <div className="d-flex ac-js gap-5 my-4">
              <button className="back-btn" onClick={handleEdit}>
                Edit
              </button>
              <button className="submit-btn" onClick={handleSubmit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutReview;
